import {createBackendEditor, referenceActionData} from '../../form/ComponentEditor';
import OperationListEditor from './OperationListEditor';
import {CurrencyFormatter, DiscountFormatter, ProductColumn, UnitFormatter} from "../../../utils/formatters";
import {postData} from "../../../utils/request";
import React, {Fragment} from "react";
import TextInput from "../../form/TextInput";
import {ifCompletedInput} from "../../../utils/other";
import RepurchaseEditorForm from "./Elements/RepurchaseForm";
import Button from "../../form/Button";
import debounce from "lodash-es/debounce";
import CheckBox from "../../form/CheckBox";
import {PriceView} from "../../classes/Price";
import Select from "../../form/Select";

const sortable = true;
const filterable = true;

const percentColors = [
    {pct: 0.0, color: {r: 216, g: 218, b: 255}},
    {pct: 0.5, color: {r: 245, g: 221, b: 187}},
    {pct: 1.0, color: {r: 255, g: 160, b: 160}}];

const getColorForPercentage = function (pct) {
    let i = 1;
    for (; i < percentColors.length - 1; i++) {
        if (pct < percentColors[i].pct) {
            break;
        }
    }
    let lower = percentColors[i - 1];
    let upper = percentColors[i];
    let range = upper.pct - lower.pct;
    let rangePct = (pct - lower.pct) / range;
    let pctLower = 1 - rangePct;
    let pctUpper = rangePct;
    let color = {
        r: Math.floor(lower.color.r * pctLower + upper.color.r * pctUpper),
        g: Math.floor(lower.color.g * pctLower + upper.color.g * pctUpper),
        b: Math.floor(lower.color.b * pctLower + upper.color.b * pctUpper)
    };
    return 'rgb(' + [color.r, color.g, color.b].join(',') + ')';
    // or output as hex if preferred
}

class RepurchaseEditor extends React.Component {

    state = {
        filtered: false,
        componentEditor: "Repurchase",
        hoverColumnValue: null,
        filter: [
            {column: "preset", value: "Repurchase"},
            {column: "saved", value: 1}
        ],
        labels: {source: "Telephely", seller: "Partner"},
        actionButtons: [],
        pool: {
            exports: [{as: "excel", label: "Excel", icon: "far fa-file-excel", filter: [{field: "pickedqty", operator: "not isnull"}]}],
            orderBy: {field: "displayname", direction: "ASC"},
            selection: [],
            pageSize: 5,
            pageSizes: [5,10,15,20,50],
            aggregations: {
                fields: {
                    "count": {
                        field: "pickedqty", type: "COUNT"
                    },
                    "saleprice_net_total": {
                        field: "saleprice_net_total", type: "SUM"
                    },
                    "saleprice_gross_total": {
                        field: "saleprice_gross_total", type: "SUM"
                    },
                    "pickedqty": {
                        field: "pickedqty", type: "SUM"
                    },
                }
            },
            component: "RepurchasePoolItem",
            view: ["id","product_id","OperationList_id", "vat", "component_type", "component_id", ...PriceView("saleprice"), "saleprice", ...PriceView("listprice"), "listprice"],
            columns: {
                "displayname": {
                    ...ProductColumn("product_id"),
                    width: 1,
                },
                "barcode": {
                    header: 'Vonalkód',
                    width: 1,
                    sortable,
                    filterable
                },
                "listprice_gross": {
                    header: 'Lista ár',
                    width: 1,
                    sortable,
                    filterable,
                    renderer: CurrencyFormatter
                },
                "discount": {
                    header: "Kedvezmény",
                    width: 1,
                    sortable,
                    filterable,
                    className: "discount",
                    onHover: (e, field, value, purchaseitem) => {
                        this.hoverDebounce && this.hoverDebounce(purchaseitem.discount);
                    },
                    onLeave: (e, field, value, purchaseitem) => {
                        this.hoverDebounce && this.hoverDebounce(null);
                    },
                    columnCss: (field, value, purchaseitem, purchaseitems) => {
                        const current = parseFloat(purchaseitem.discount || 0).toFixed(2);
                        const percents = purchaseitems.map((purchaseitem) => parseFloat(purchaseitem.discount || 0).toFixed(2)).filter(function (item, pos, self) {
                            return self.indexOf(item) == pos;
                        }).sort();
                        const posPercent = parseFloat((percents.length - percents.indexOf(current)) / percents.length).toFixed(2);
                        //console.log((percents.length - percents.indexOf(current)) / percents.length);
                        return {
                            backgroundColor: getColorForPercentage(posPercent/*percents.indexOf(current) / percents.length*/),
                            fontWeight: this.state.hoverColumnValue == current ? "bold" : "normal"
                        }
                    },
                    renderer: DiscountFormatter,
                    editor:
                        (parent) =>
                            (props, value) =>
                                <TextInput type="decimal" name="discount" label={false} focus="focus"
                                           value={props.rowData.discount}
                                           onKeyDown={(e, field, value) => {
                                               ifCompletedInput(e) && parent.onEditorValueChange(props, value);
                                           }}
                                           onBlur={(e, field, value) => parent.onEditorValueChange(props, value)}
                                />
                },
                "saleprice_net": {
                    header: 'Nettó egységár',
                    width: 1,
                    renderer: CurrencyFormatter,
                    className: "currency",
                    editor:
                        (parent) =>
                            (props, value) =>
                                <TextInput type="decimal" name="saleprice_net" label={false} focus="focus"
                                           value={value}
                                           onKeyDown={(e, field, value) => {
                                               ifCompletedInput(e) && parent.onEditorValueChange(props, value);
                                           }}
                                           onBlur={(e, field, value) => parent.onEditorValueChange(props, value)}/>
                },
                "vat": {
                    header: "Áfa",
                    width: "vat",
                    renderer: (value) => <span className="vat">{value.name}</span>,
                    editor:
                        (parent) =>
                            (props, value) =>
                                <Select
                                    name="vat"
                                    component="vatrate"
                                    filter={{field: "active", value: 1}}
                                    objectValue={true}
                                    label={false}
                                    storeType="local"
                                    value={value}
                                    view={["rate"]}
                                    saveChanges={(field, value) => {
                                        parent.onEditorValueChange(props, value);
                                    }}
                                />
                },
                "saleprice_gross": {
                    header: 'Bruttó egységár',
                    width: 1,
                    renderer: CurrencyFormatter,
                    className: "currency",
                    editor:
                        (parent) =>
                            (props, value) =>
                                <TextInput type="decimal" name="saleprice" label={false} focus="focus"
                                           value={value}
                                           onKeyDown={(e, field, value) => {
                                               ifCompletedInput(e) && parent.onEditorValueChange(props, value);
                                           }}
                                           onBlur={(e, field, value) => parent.onEditorValueChange(props, value)}/>
                },
                "stockqty": {
                    header: 'Raktárkészlet',
                    width: 1,
                    sortable,
                    filterable,
                    renderer: (value, row) => UnitFormatter(value, row.unit),
                    className: "unit"
                },
                "pickedqty": {
                    header: "Darabszám",
                    width: 1,
                    renderer: (value, row) => UnitFormatter(value, row.unit),
                    sortable,
                    filterable,
                    aggregation: (value, row) => {
                        return <Fragment><span className="unit">Összesen: {UnitFormatter(row.pickedqty || 0, row.unit).props.children}</span><span className="unit">Tétel: {UnitFormatter(row.count || 0, row.unit).props.children}</span></Fragment>;
                    },
                    className: "unit",
                    editor:
                        (parent) =>
                            (props, value) =>
                                <TextInput type="number" name="pickedqty" label={false} focus value={value}
                                           onKeyDown={(e, field, value) => {ifCompletedInput(e) && parent.onEditorValueChange(props, value);}}
                                           onBlur={(e, field, value) => parent.onEditorValueChange(props, value)}/>
                },
                "saleprice_gross_total": {
                    header: 'Bruttó összeg',
                    width: 1,
                    renderer: CurrencyFormatter,
                }
            }
        }
    };

    promisedSetState = (newState) => {
        return new Promise((resolve) => {
            this.setState(newState, () => {
                resolve()
            });
        });
    }

    constructor(props) {
        super(props);
        this.hoverDebounce = debounce((value) => {
            this.setState({hoverColumnValue: value});
        }, 300);
    }

    getProps = () => {
        const props = {
            ...this.props,
            ...this.state,
        };
        props.actionButtons = [
            <Button key="4" disabled={ props.isLoading || !props.values.id } color="primary" icon={{lib: "fa", name: "file"}} className="pull-right" onClick={ async (e) => {
                const values = props.values;
                values.autocheckup = true;
                await props.processAction(async () => {
                    const response = await postData({
                        action: 'OperationList.doAction',
                        actionname: 'create',
                        data: await referenceActionData(values, this.props.componentConfig)
                    });
                    await this.props.pristineForm(false);
                    return response;
                });
            }}>
                Visszavásárlás
            </Button>
        ];
        props.pool.parameters = {};
        if (props.values && props.values.seller) {
            props.pool.visible = true;
            props.pool.parameters.discount_type = "purchase";
            props.pool.filter = [{field: "customer", value: props.values.seller.partner_id}];
            props.pool.parameters.partner = props.values.seller.partner_id;
            props.pool.parameters.OperationList_id = props.values.id;
            props.pool.parameters.seller = props.values.seller.partner_id;
            props.pool.parameters.customer = props.values.customer.partner_id;
        } else {
            props.pool.visible = false;
        }
        props.filtered = this.state.filtered;
        if (this.state.filtered) {
            props.pool.filter = [{field: "pickedqty", operator: "not isnull"}];
        } else {
            props.pool.filter = [];
        }
        props.pool.paginatorRight = <CheckBox onIcon={{lib: "fa", name: "box"}} offIcon={{lib: "fa", name: "box-open"}} value={this.state.filtered} label={false} saveChanges={(field, value) => {
            this.setState({filtered: !!value});
        }} />;
        return props;
    }

    renderer = (componentProps) => {
        return <RepurchaseEditorForm {...componentProps} />;
    }

    render() {
        const props = this.getProps();
        return <OperationListEditor {...props} renderer={this.renderer} />;
    }

}

export default createBackendEditor({
    componentName: "OperationList",
    WrappedComponent: RepurchaseEditor,
    editor: "Repurchase",
    dirtyFlag: (values) => {
        return !parseInt(values.saved) && values.id;
    },
    props: {
        componentActions: async (action, data, params, componentConfig) => {
            params.preset = "Repurchase";
            data = await referenceActionData(data, componentConfig);
            return {action,data,params};
        }
    }
});